import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { searchNearbyPlaces } from '@/services/placesAPI';
import { batchSaveGooglePlaces } from '@/services/enhancedCampDatabaseService';

const CacheDiagnostics = () => {
  const [cachedCamps, setCachedCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadCachedCamps = async () => {
    setLoading(true);
    try {
      // Get all camps from database
      const { data, error } = await supabase
        .from('camp_audit_data')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      setCachedCamps(data || []);

      // Calculate stats
      const stats = {
        total: data?.length || 0,
        withPhotos: data?.filter(c => c.photos && c.photos !== 'null').length || 0,
        withReviews: data?.filter(c => c.reviews && c.reviews !== 'null').length || 0,
        fromGoogle: data?.filter(c => c.place_id).length || 0,
        recent: data?.filter(c => {
          const createdAt = new Date(c.created_at);
          const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
          return createdAt > oneDayAgo;
        }).length || 0
      };

      setStats(stats);

    } catch (error) {
      console.error('Error loading cached camps:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCachedCamps();
  }, []);

  const clearCache = async () => {
    if (!confirm('Are you sure you want to clear all cached camps?')) return;

    try {
      // Method 1: Try direct delete first
      console.log('🗑️ Attempting to clear cache...');
      
      const { data: campsToDelete, error: fetchError } = await supabase
        .from('camp_audit_data')
        .select('id, name')
        .limit(1000);

      if (fetchError) {
        console.error('Error fetching camps to delete:', fetchError);
        throw fetchError;
      }

      if (!campsToDelete || campsToDelete.length === 0) {
        alert('No camps to clear!');
        return;
      }

      console.log(`Found ${campsToDelete.length} camps to delete`);

      // Try to delete using IDs
      const { error: deleteError } = await supabase
        .from('camp_audit_data')
        .delete()
        .in('id', campsToDelete.map(camp => camp.id));

      if (deleteError) {
        console.error('Delete failed:', deleteError);
        
        // Method 2: If delete fails, try marking as needing refresh instead
        if (deleteError.message.includes('permission') || deleteError.message.includes('policy')) {
          console.log('🔄 Delete not allowed, trying alternative method...');
          
          const { error: updateError } = await supabase
            .from('camp_audit_data')
            .update({ needs_refresh: true, last_fetched_from_google: null })
            .in('id', campsToDelete.map(camp => camp.id));

          if (updateError) {
            throw new Error(`Both delete and update failed. Delete error: ${deleteError.message}, Update error: ${updateError.message}`);
          }

          alert(`Cache marked for refresh! Updated ${campsToDelete.length} camps.\n\nNote: DELETE permission not available, so camps are marked as needing refresh instead.`);
        } else {
          throw deleteError;
        }
      } else {
        alert(`Cache cleared successfully! Deleted ${campsToDelete.length} camps.`);
      }

      loadCachedCamps();
    } catch (error) {
      console.error('Clear cache error:', error);
      alert(`Error clearing cache: ${error.message}\n\nTo fix this permanently, run the SQL commands in your Supabase dashboard:\n\nGRANT DELETE ON public.camp_audit_data TO authenticated;\n\nCREATE POLICY "Allow delete access" ON public.camp_audit_data FOR DELETE TO authenticated USING (true);`);
    }
  };

  const runQuickTest = async () => {
    setTestRunning(true);
    setTestResult(null);

    try {
      console.log('🧪 Testing Google Places API → Database flow...');
      
      // Get count before test
      const { count: beforeCount } = await supabase
        .from('camp_audit_data')
        .select('*', { count: 'exact', head: true });
      
      console.log(`📊 Camps in database before test: ${beforeCount}`);
      
      // Test the ACTUAL search function that users use
      console.log('🔍 Testing searchAllYouthPrograms function...');
      
      // Import the actual function users call
      const { searchAllYouthPrograms } = await import('@/services/placesAPI');
      
      // Test with Portland, Oregon coordinates (like the user's search)
      const searchResults = await searchAllYouthPrograms(
        { lat: 45.5152, lng: -122.6784 }, // Portland, Oregon coordinates
        10, // 10 mile radius
        { forceRefresh: true } // Force API call to test caching
      );
      
      console.log('✅ Search completed, results:', searchResults.length);
      
      // Wait a moment for background save to complete
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Get count after test
      const { count: afterCount } = await supabase
        .from('camp_audit_data')
        .select('*', { count: 'exact', head: true });
      
      console.log(`📊 Camps in database after test: ${afterCount}`);
      
      const newCampsAdded = afterCount - beforeCount;
      
      if (newCampsAdded > 0) {
        setTestResult({
          success: true,
          message: `✅ Caching is working! Added ${newCampsAdded} new camps to database`,
          details: {
            beforeCount,
            afterCount,
            newCampsAdded,
            searchResults: searchResults.length,
            message: 'Google Places API results are being automatically saved to database'
          }
        });
      } else {
        setTestResult({
          success: false,
          message: `❌ No new camps were saved. Check the console logs for errors.`,
          details: {
            beforeCount,
            afterCount,
            searchResults: searchResults.length,
            message: 'Either caching failed or camps already existed in database'
          }
        });
      }

      // Reload cache to show any new camps
      setTimeout(() => loadCachedCamps(), 1000);

    } catch (error) {
      console.error('Test error:', error);
      setTestResult({
        success: false,
        message: `❌ Test failed: ${error.message}`,
        details: {
          error: error.message,
          stack: error.stack,
          message: 'Check console for detailed error logs'
        }
      });
    } finally {
      setTestRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              📊 Cache Diagnostics
            </h1>
            <div className="space-x-3">
              <button
                onClick={runQuickTest}
                disabled={testRunning}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {testRunning ? '⏳ Testing...' : '🔍 Test Search→Save'}
              </button>
              <button
                onClick={loadCachedCamps}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                🔄 Refresh
              </button>
              <button
                onClick={clearCache}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                title="⚠️ WARNING: This will delete ALL cached camps from the database!"
              >
                🗑️ Clear Cache
              </button>
            </div>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`mb-6 p-4 rounded-lg ${testResult.success ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300'} border`}>
              <h3 className={`font-semibold mb-2 ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.success ? '✅ Test Successful' : '❌ Test Failed'}
              </h3>
              <p className={testResult.success ? 'text-green-700' : 'text-red-700'}>
                {testResult.message}
              </p>
              {testResult.details && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm">View Details</summary>
                  <pre className="mt-2 p-2 bg-white rounded text-xs overflow-auto">
                    {JSON.stringify(testResult.details, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading cached data...</p>
            </div>
          ) : (
            <>
              {/* Stats */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
                    <div className="text-sm text-blue-800">Total Cached</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{stats.fromGoogle}</div>
                    <div className="text-sm text-green-800">From Google</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-brand-600">{stats.withPhotos}</div>
                    <div className="text-sm text-brand-800">With Photos</div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">{stats.withReviews}</div>
                    <div className="text-sm text-yellow-800">With Reviews</div>
                  </div>
                  <div className="bg-pink-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-pink-600">{stats.recent}</div>
                    <div className="text-sm text-pink-800">Added Today</div>
                  </div>
                </div>
              )}

              {/* Cached Camps List */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Cached Camps (Last 50)
                </h2>

                {cachedCamps.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">No cached camps found.</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Perform a search to populate the cache!
                    </p>
                  </div>
                ) : (
                  cachedCamps.map((camp) => (
                    <div key={camp.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{camp.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {camp.formatted_address || camp.location}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {camp.place_id && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                ✅ Google Places
                              </span>
                            )}
                            {camp.rating && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                ⭐ {camp.rating}
                              </span>
                            )}
                            {camp.photos && camp.photos !== 'null' && (
                              <span className="px-2 py-1 bg-purple-100 text-brand-800 text-xs rounded-full">
                                📸 Has Photos
                              </span>
                            )}
                            {camp.primary_category && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {camp.primary_category}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-xs text-gray-500">
                            {new Date(camp.created_at).toLocaleString()}
                          </div>
                          {camp.view_count > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              👁️ {camp.view_count} views
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Expandable Details */}
                      <details className="mt-3">
                        <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800">
                          View Details
                        </summary>
                        <div className="mt-2 p-3 bg-gray-50 rounded text-xs space-y-1">
                          <p><strong>Place ID:</strong> {camp.place_id || 'N/A'}</p>
                          <p><strong>Coordinates:</strong> {camp.coordinates_lat}, {camp.coordinates_lng}</p>
                          <p><strong>Business Status:</strong> {camp.business_status || 'N/A'}</p>
                          <p><strong>User Ratings:</strong> {camp.user_ratings_total || 0}</p>
                          <p><strong>Photo Count:</strong> {camp.photo_count || 0}</p>
                          <p><strong>Categories:</strong> {camp.all_categories?.join(', ') || 'N/A'}</p>
                          <p><strong>Last Fetched:</strong> {camp.last_fetched_from_google ? new Date(camp.last_fetched_from_google).toLocaleString() : 'N/A'}</p>
                        </div>
                      </details>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-blue-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            🔍 How to Verify Caching
          </h3>
          <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
            <li>Go to the Discovery or Search page</li>
            <li>Perform a search for camps in any location</li>
            <li>Return to this page and click "Refresh"</li>
            <li>You should see new camps appear in the cache</li>
            <li>Check the console logs during search for save confirmations</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default CacheDiagnostics;
